import { z } from 'zod';
import { csv } from '../common/query.js';
import { AUDIT_ACTIONS, AUDIT_FAMILIES } from './actions.js';
// Audit entity-type vocabulary — the closed set of things `audit_logs.entity_type`
// may name (ADR-058). Single source of truth: the write helpers type `entityType`
// against `AuditEntityType` (a typo / drift like `tag` vs `site_tag` is a compile
// error, not a silent free string), and the read/render registry maps presentation
// (icon · peek) per type. Add a new entity here in the PR that first audits it —
// same discipline as the action vocabulary (ADR-020: vocabulary in code).
//
// snake_case, ≤ 64 chars to fit `entity_type varchar(64)`. Test stand-ins (the
// generic import engine's throwaway entity) are NOT registered — that unit test
// casts its stand-in deliberately.
export const AUDIT_ENTITY_TYPES = [
    // Identity + access
    'user',
    'role',
    'identity_provider',
    'onboarding_rule',
    // Platform
    'project',
    'license_tier',
    // Sites + site reference data
    'site',
    'site_type',
    'site_priority',
    'site_category',
    'tag',
    'site_solution_config',
    'site_designation',
    // AdministrativeArea / territory
    'zone',
    'cluster',
    'administrative_area',
    // Invoicing — the Contract surface (ADR-060; catalogue_charter rows audit
    // per-field with entityId = the field key; band_tolerance with entityId =
    // the family)
    'catalogue_charter',
    'load_band',
    'grid_availability_band',
    'solar_band',
    'cooling_band',
    'band_tolerance',
    'contractual_configuration',
    'commercial_flag',
    // Accountability
    'responsibility',
    'responsibility_assignment',
    // Assets — the register wave (ADR-063 verbs · ADR-065 catalog · ADR-066
    // owners attachment). catalog_import = the one-door import stamp
    // (entityId = the catalog_imports row id).
    'asset',
    'asset_model',
    'asset_owner',
    // Files — the registry row IS the file (ADR-068); entityId = the fil_ id, the
    // label = display name or original name (never for user/credential classes —
    // those snapshot the id, SECURITY §Files).
    'file',
    'catalog_import',
    // Tickets — the operational work record's capture skeleton:
    // the ticket (entityId = tkt_) and the visit (vis_ — the check-in/out
    // ceremonies; the journal entry is module data, never an audit subject).
    'ticket',
    'visit',
    // Teams — fixed people of one project (ADR-083); entityId = the team_ id, the
    // label = its name. The member moves are `assign` link rows on the team (the
    // team's history — who joined and left); the leave's row on the person names
    // the teams it ended.
    'team',
    // Consumables (the stock dataset; the transaction verbs, when they land,
    // grow the interactive surface). consumable_type rows are born via import
    // vocabulary-consent or the verbs' inline create; consumable_stock is the import-run discriminator (the
    // ledger rows carry their own actor pair — SCHEMA §consumable_transactions).
    'consumable_type',
    'consumable_stock',
    // Notifications
    'email',
    // External integrations (ADR-024 registry)
    'integration',
    // Application credentials (ADR-004 — the platform api-clients surface)
    'api_client',
    // Personal access tokens (ADR-004 — the self-service /profile surface).
    // Rows are project-scoped to the token's pinned project (audit follows consequence).
    'user_api_key',
    // The audit trail itself — its export is a Tier-1 `export` line like any
    // other (a copy of the trail left the system); entityId null, no label.
    'audit_log',
];
export const AuditEntityTypeSchema = z.enum(AUDIT_ENTITY_TYPES);
// ── the families — the audit page's What filter, grouped (actions.ts's words) ──
// The vocabulary shown WHOLE, by family — a person looking for the contract's
// bands finds them under Contract, not among forty flat rows. A TOTAL map.
export const AUDIT_ENTITY_FAMILY = {
    user: 'people',
    role: 'people',
    identity_provider: 'people',
    onboarding_rule: 'people',
    responsibility: 'people',
    responsibility_assignment: 'people',
    api_client: 'people',
    user_api_key: 'people',
    site: 'sites',
    site_type: 'sites',
    site_priority: 'sites',
    site_category: 'sites',
    tag: 'sites',
    site_solution_config: 'sites',
    site_designation: 'sites',
    zone: 'sites',
    cluster: 'sites',
    administrative_area: 'sites',
    catalogue_charter: 'contract',
    load_band: 'contract',
    grid_availability_band: 'contract',
    solar_band: 'contract',
    cooling_band: 'contract',
    band_tolerance: 'contract',
    contractual_configuration: 'contract',
    commercial_flag: 'contract',
    asset: 'assets',
    asset_model: 'assets',
    asset_owner: 'assets',
    catalog_import: 'assets',
    consumable_type: 'assets',
    consumable_stock: 'assets',
    file: 'files',
    ticket: 'field_work',
    visit: 'field_work',
    team: 'people',
    project: 'project',
    license_tier: 'project',
    email: 'project',
    integration: 'project',
    audit_log: 'project',
};
// ── the objects — the audit page's Object filter, a tree of real pairs ────────
// A reader picks what the line is ABOUT: a family (people · sites · …), one
// object (a site), or one thing that happens to it (a site suspended). The tree
// lists only the pairs the backend WRITES — `AUDIT_OBJECT_VERBS` is the honest
// inventory of (entity_type, action), authored from the write sites and held by
// vocabulary.test.ts: a verb that no object lists is RESERVED (a verb the
// vocabulary keeps for a page still to come), and the test refuses a reserved
// verb that some object now writes — a landing page moves its verb into its
// object's row and out of the reserved list. A verb in no object is never
// offered by the picker; the Action word still finds it the day it is written.
//
// On the wire — ONE param, `object`, whose values carry their level (ADR-037 §2
// · ADR-080 §8 — the territory and location grammars' sibling): `family:<f>`
// (a whole family — prefixed, because the family "project" and the object
// "project" share a word) · `<object>` · `<object>.<verb>`. Only the highest
// pick is stored (a picked family absorbs its objects, a picked object its
// verbs); picks ADD UP; the read expands a family to its objects when it runs.
/** The families an object belongs to — the seven words of `AUDIT_FAMILIES` without `any` (a verb's word only). */
export const AUDIT_OBJECT_FAMILIES = AUDIT_FAMILIES.filter((f) => f !== 'any');
/** The verbs each object takes — every (entity_type, action) pair some backend code writes. */
export const AUDIT_OBJECT_VERBS = {
    administrative_area: ['create', 'update', 'delete', 'disable', 'reactivate', 'export', 'import_started', 'import_completed', 'import_failed'],
    api_client: ['create', 'update', 'api_client_rotated', 'api_client_revoked'],
    asset: ['create', 'update', 'move', 'replace', 'adjust', 'put_on_hold', 'reinstate', 'retire', 'declare_lost', 'recover', 'write_off', 'dispose', 'transfer_ownership', 'reactivate', 'event_date_corrected', 'credential_revealed', 'credential_confirmed', 'export', 'import_started', 'import_completed', 'import_failed'],
    asset_model: ['create', 'update', 'merge', 'withdrawn', 'reoffered', 'export', 'import_started', 'import_completed', 'import_failed'],
    asset_owner: ['create', 'update', 'associate', 'remove_from_project', 'disable', 'reactivate', 'export', 'import_started', 'import_completed', 'import_failed'],
    audit_log: ['export'],
    band_tolerance: ['update'],
    catalog_import: ['import_completed'],
    catalogue_charter: ['update'],
    cluster: ['create', 'update', 'delete', 'disable', 'reactivate', 'export', 'import_started', 'import_completed', 'import_failed'],
    commercial_flag: ['create', 'update', 'delete', 'disable', 'reactivate', 'export', 'import_started', 'import_completed', 'import_failed'],
    consumable_stock: ['export', 'import_started', 'import_completed', 'import_failed'],
    consumable_type: ['create'],
    contractual_configuration: ['create', 'update', 'delete', 'disable', 'reactivate', 'export', 'import_started', 'import_completed', 'import_failed'],
    cooling_band: ['create', 'update', 'delete', 'disable', 'reactivate', 'export', 'import_started', 'import_completed', 'import_failed'],
    email: ['email_sent'],
    file: ['create', 'update', 'delete', 'file_classified', 'file_downloaded', 'file_previewed', 'file_moved', 'file_replaced', 'file_restored', 'file_purged'],
    grid_availability_band: ['create', 'update', 'delete', 'disable', 'reactivate', 'export', 'import_started', 'import_completed', 'import_failed'],
    identity_provider: ['create', 'update', 'delete'],
    integration: ['create', 'update', 'delete'],
    license_tier: ['create', 'update'],
    load_band: ['create', 'update', 'delete', 'disable', 'reactivate', 'export', 'import_started', 'import_completed', 'import_failed'],
    onboarding_rule: ['create', 'update', 'delete'],
    project: ['create', 'update', 'suspend', 'reactivate', 'terminate', 'export'],
    responsibility: ['create', 'update', 'delete', 'disable', 'reactivate', 'export', 'import_started', 'import_completed', 'import_failed'],
    responsibility_assignment: ['assign', 'export', 'import_started', 'import_completed', 'import_failed'],
    role: ['create', 'update', 'disable', 'reactivate', 'export'],
    site: ['create', 'update', 'delete', 'activate', 'suspend', 'reactivate', 'decommission', 'lifecycle_dates_corrected', 'suggestion_ignored', 'suggestion_restored', 'export', 'import_started', 'import_completed', 'import_failed'],
    site_category: ['create', 'update', 'delete', 'disable', 'reactivate', 'export', 'import_started', 'import_completed', 'import_failed'],
    site_designation: ['create', 'designation_changed', 'designation_corrected', 'designation_start_corrected', 'designation_period_deleted', 'export', 'import_started', 'import_completed', 'import_failed'],
    site_priority: ['create', 'update', 'delete', 'disable', 'reactivate', 'export', 'import_started', 'import_completed', 'import_failed'],
    site_solution_config: ['create', 'config_changed', 'config_corrected', 'config_start_date_corrected', 'config_period_deleted', 'export', 'import_started', 'import_completed', 'import_failed'],
    site_type: ['create', 'update', 'delete', 'disable', 'reactivate', 'export', 'import_started', 'import_completed', 'import_failed'],
    solar_band: ['create', 'update', 'delete', 'disable', 'reactivate', 'export', 'import_started', 'import_completed', 'import_failed'],
    tag: ['create', 'update', 'delete', 'disable', 'reactivate', 'export', 'import_started', 'import_completed', 'import_failed'],
    team: ['create', 'update', 'disable', 'reactivate', 'delete', 'assign'],
    ticket: ['ticket_created', 'ticket_closed', 'ticket_reopened', 'tickets_closed', 'tickets_graced', 'tickets_rejudged'],
    user: ['create', 'update', 'associate', 'remove_from_project', 'handover', 'assign', 'disable', 'reactivate', 'login', 'logout', 'login_fail', 'mfa_fail', 'account_locked', 'password_reset', 'password_changed', 'mfa_enrolled', 'mfa_self_enrolled', 'mfa_self_disabled', 'external_identity_invited', 'external_identity_invite_revoked', 'external_identity_linked', 'external_identity_disconnected', 'external_identity_test_message', 'export'],
    user_api_key: ['create', 'update', 'pat_revoked'],
    visit: ['visit_checked_in', 'visit_rechecked', 'visit_closed'],
    zone: ['create', 'update', 'delete', 'disable', 'reactivate', 'export', 'import_started', 'import_completed', 'import_failed'],
};
/** The verbs the vocabulary holds for pages still to come — written by no code today, so offered by no picker. */
export const AUDIT_RESERVED_ACTIONS = [
    'clone',
    'follow',
    'unfollow',
    'revoke',
    'toggle_mfa',
    'toggle_active',
    'mfa_authenticator_reset',
    'reorder',
];
const OBJECTS_OF_FAMILY = (() => {
    const out = Object.fromEntries(AUDIT_OBJECT_FAMILIES.map((f) => [f, []]));
    for (const e of AUDIT_ENTITY_TYPES)
        out[AUDIT_ENTITY_FAMILY[e]].push(e);
    return out;
})();
/** The objects of a family — what a picked family expands to. */
export function objectsOfFamily(family) {
    return OBJECTS_OF_FAMILY[family];
}
const FAMILY_PREFIX = 'family:';
function isObject(v) {
    return AUDIT_ENTITY_TYPES.includes(v);
}
function isAction(v) {
    return AUDIT_ACTIONS.includes(v);
}
/** One token read: `family:<family>` · `<object>` · `<object>.<verb>` — or null for anything the closed lists do not hold. */
export function parseAuditObjectToken(token) {
    if (token.startsWith(FAMILY_PREFIX)) {
        const family = token.slice(FAMILY_PREFIX.length);
        return AUDIT_OBJECT_FAMILIES.includes(family) ? { kind: 'family', family: family } : null;
    }
    const dot = token.indexOf('.');
    if (dot < 0)
        return isObject(token) ? { kind: 'object', object: token } : null;
    const object = token.slice(0, dot);
    const action = token.slice(dot + 1);
    return isObject(object) && isAction(action) ? { kind: 'pair', object, action } : null;
}
/** The token of a family, an object or a pair — what a link and the wire carry. */
export function auditObjectToken(t) {
    if (t.kind === 'family')
        return FAMILY_PREFIX + t.family;
    if (t.kind === 'object')
        return t.object;
    return `${t.object}.${t.action}`;
}
/** One `object` value — refused (400) when the closed lists do not hold it. */
export const AuditObjectTokenSchema = z
    .string()
    .max(104)
    .refine((v) => parseAuditObjectToken(v) !== null, { message: 'an object is `family:<family>`, `<object>` or `<object>.<verb>`' });
/** The `object` query param — comma-separated tokens, at most 50 picks. */
export const AuditObjectParamSchema = csv(AuditObjectTokenSchema).pipe(z.array(z.string()).max(50, 'at most 50 objects').optional());
/** The tokens read with the highest pick winning: a family covers its objects, an object its pairs; each once; an unreadable token skipped. */
export function parseAuditObjects(tokens) {
    const parsed = (tokens ?? []).map(parseAuditObjectToken).filter((t) => t !== null);
    const entityTypes = new Set();
    for (const t of parsed) {
        if (t.kind === 'family')
            for (const o of objectsOfFamily(t.family))
                entityTypes.add(o);
        else if (t.kind === 'object')
            entityTypes.add(t.object);
    }
    const pairs = new Map();
    for (const t of parsed) {
        if (t.kind === 'pair' && !entityTypes.has(t.object))
            pairs.set(`${t.object}.${t.action}`, { entity_type: t.object, action: t.action });
    }
    return { entityTypes: [...entityTypes], pairs: [...pairs.values()] };
}
/** The tokens a link holds that the grammar accepts — a screen's TOLERANT read (ADR-037: a malformed or hand-edited
 *  value is dropped silently, never sent to fail the whole list); the server still refuses what it is handed. */
export function wellFormedAuditObjects(tokens) {
    return tokens.filter((t) => parseAuditObjectToken(t) !== null);
}
